import merge from 'deepmerge';
import {createTheme, darken, PaletteOptions, Theme} from '@mui/material/styles';
import {formattedThemes} from "./themeDb";
import utils from "../utils";
import uiStore from "../stores/uiStore";
import {TypographyOptions} from "@mui/material/styles/createTypography";
import {SpacingOptions} from "@mui/system/createTheme/createSpacing";
import {PartialTheme, ShadowTheme, ThemeData} from "../clients/shadow/interfaces";
import {PaletteMode, SxProps} from "@mui/material";

export function createScrollbarStyle(color: string): SxProps {
    return {
        '&::-webkit-scrollbar': {
            width: "15px",
            height: "15px",
            backgroundColor: darken(color, 0.05),

        },
        '&::-webkit-scrollbar-thumb': {
            height: "40px",
            backgroundColor: darken(color, 0.15),
        }
    }
}

// Side panels (rooms, users) were a fixed grey in every dark theme. A theme can set its own with a custom
// `palette.panel: {background, color}` (somniworks does); the others keep the old greys.
export function getPanelColors(theme: Theme): { background: string, color: string } {
    const panel = (theme.palette as unknown as { panel?: { background: string, color: string } }).panel;
    const dark = theme.palette.mode === "dark";
    return {
        background: panel?.background ?? (dark ? "#2c2c2c" : "#efefef"),
        color: panel?.color ?? (dark ? "#CCC" : "#white"),
    };
}

export function createThemeOptions(
        name: string,
        mode: PaletteMode,
        main: string,
        backgroundDefault: string,
        backgroundPaper: string
) {
    return {
        name,
        palette: {
            mode,
            primary: {
                main,
            },
            background: {
                default: backgroundDefault,
                paper: backgroundPaper,
            },
        },
    }
}

export function createSimpleTheme(
        name: string,
        mode: PaletteMode,
        main: string,
        backgroundDefault: string,
        backgroundPaper: string
): ShadowTheme {
    const theme = createTheme(createThemeOptions(name, mode, main, backgroundDefault, backgroundPaper));
    const themeData: ThemeData = {
        theme,
        metadata: {
            name,
            comment: "",
            creator: {
                id: "temp",
                name: "temp",
            }
        }
    }
    return createShadowTheme(themeData);
}

interface ThemeDefaults {
    palette: PaletteOptions,
    typography: TypographyOptions,
    spacing: SpacingOptions,
    components: Record<string, any>;
}

function createDefaults(backgroundColor: string): ThemeDefaults {
    return {
        // darkHeaders: false,
        palette: {
            // boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.11), 0 3px 1px -2px rgba(0, 0, 0, 0.07), 0 1px 5px 0 rgba(0, 0, 0, 0.2)",
        },
        typography: {
            fontSize: 14,
        },
        spacing: 8,
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        WebkitTapHighlightColor: 'transparent',
                    },
                    '*': uiStore.isMobile ? {} : createScrollbarStyle(backgroundColor),
                }
            },
        }
    }
}


export const normalizedThemeObjects = utils.normalize<ThemeData>(formattedThemes, "name");

export function getShadowTheme(themeName?: string): ShadowTheme {
    const themeData = normalizedThemeObjects[themeName];

    if (!themeData) {
        throw Error("No themeObject with name: " + themeName);
    }

    return createShadowTheme(themeData);

}

export function createShadowTheme(themeData: ThemeData): ShadowTheme {
    return {
        id: themeData.name,
        creator_id: themeData.metadata.creator.id,
        name: themeData.name,
        data: themeData,
        creation_date: new Date().toISOString(),
    }
}

export function createFullTheme(partialTheme: PartialTheme): Theme {

    if (uiStore.isMobile) {
        const overrides = partialTheme.components?.MuiCssBaseline?.styleOverrides?.["*"];

        if (overrides) {
            delete overrides["&::-webkit-scrollbar"];
            delete overrides["&::-webkit-scrollbar-thumb"];
        }
    }

    return createTheme(merge(createDefaults(partialTheme.palette.background.paper), partialTheme));
}
